import {
  createContext,
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

interface InlineEditContextValue {
  isInEditMode: boolean;
  activeInlineEditHandlerRef: RefObject<HTMLDivElement | null> | undefined;
  setActiveInlineEditHandlerRef: (
    handler: RefObject<HTMLDivElement | null>
  ) => void;
}

const InlineEditContext = createContext<InlineEditContextValue | null>(null);

type InlineEditProviderProps = {
  children: React.ReactNode;
  isInEditMode: boolean;
};

export const InlineEditProvider = ({
  children,
  isInEditMode,
}: InlineEditProviderProps) => {
  const [activeInlineEditHandlerRef, setActiveInlineEditHandlerRef] =
    useState<RefObject<HTMLDivElement | null>>();

  const onClickOutsideOfActiveEditHandler = (
    event: MouseEvent,
    activeInlineEditHandlerRef?: RefObject<HTMLDivElement | null>
  ) => {
    if (
      activeInlineEditHandlerRef?.current &&
      !activeInlineEditHandlerRef.current.contains(event.target as Node)
    ) {
      setActiveInlineEditHandlerRef(undefined);
    }
  };

  const handleClickOutsideListener = useCallback(
    (event: MouseEvent) =>
      onClickOutsideOfActiveEditHandler(event, activeInlineEditHandlerRef),
    [activeInlineEditHandlerRef]
  );

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutsideListener);

    return () => {
      document.removeEventListener('mousedown', handleClickOutsideListener);
    };
  }, [handleClickOutsideListener]);

  return (
    <InlineEditContext.Provider
      value={{
        isInEditMode,
        activeInlineEditHandlerRef,
        setActiveInlineEditHandlerRef,
      }}
    >
      {children}
    </InlineEditContext.Provider>
  );
};

export const useInlineEditContext = () => {
  const context = useContext(InlineEditContext);

  if (!context) {
    throw new Error(
      'useInlineEditContext must be used within a InlineEditProvider'
    );
  }

  return context;
};
